import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { FeatureFlag, PlatformTab } from 'src/models/platform/_types';
import {
  getCombinedClientNavItems,
  isHQPlatform,
} from 'src/models/platform/utils';
import { fetchUserAuthorizations } from 'src/models/user/user';
import { isTrainer } from 'src/models/user/utils';
import { isDreamcoatActive } from 'src/shared/dreamcoat';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';

import { useHQPermissionCheck } from '../HQ/useHQPermissionCheck';
import { usePermissionCheck } from '../usePermissionCheck';

import { DecoratedNavItem } from './navUtils';
import {
  GHL_SYNC_STATUS_TAB_ID,
  clientRoutesExtraData,
  defaultIcon,
  getDreamcoatReactRouteOverride,
  ghlSyncStatusNavChildren,
  ghlSyncStatusNavTab,
  routesExtraData,
} from './routeConfig';

type TranslateMenu = ReturnType<typeof useTranslation>['t'];
type CanViewItem = (item: PlatformTab) => boolean;

const canViewAllItems: CanViewItem = () => true;

const isGhlConnected = (
  partners: Awaited<ReturnType<typeof fetchUserAuthorizations>>
) => {
  const partner = partners.find((authorization) => authorization.source === 'ghl');

  return !!(partner?.authToken || partner?.authId);
};

const appendGhlSyncStatusToDreamcoatNav = (
  items: PlatformTab[],
  t: TranslateMenu
): PlatformTab[] => {
  return items.map((item) => {
    if (item.id !== 'Settings' || !item.children || Array.isArray(item.children)) {
      return item;
    }

    const sections = item.children.sections?.map((section) => {
      if (section.id !== 'integrations') {
        return section;
      }

      if (section.children.some((child) => child.id === GHL_SYNC_STATUS_TAB_ID)) {
        return section;
      }

      return {
        ...section,
        children: [...section.children, ghlSyncStatusNavTab(t, 'trainerDashboard')],
      };
    });

    return {
      ...item,
      children: {
        ...item.children,
        sections,
      },
    };
  });
};

type TrainerNavPermissionChecks = {
  canViewTrainerTab: (tab: Partial<PlatformTab>) => boolean;
  canViewTrainerSidebarTabs: (tab: PlatformTab) => boolean;
  canViewScheduleSubtab: (tab: PlatformTab) => boolean;
};

const PARENTS_REQUIRING_CHILDREN = new Set([
  'People',
  'Programming',
  'Connect',
  'Offerings',
  'Settings',
  'Schedule',
]);

const hasVisibleChildren = (children: PlatformTab['children']): boolean => {
  if (!children) {
    return false;
  }

  if (Array.isArray(children)) {
    return children.length > 0;
  }

  return (
    (children.items?.length ?? 0) > 0 ||
    (children.sections?.some((section) => section.children.length > 0) ?? false)
  );
};

const childCanView = (
  parentId: string,
  child: PlatformTab,
  checks: TrainerNavPermissionChecks
): boolean => {
  if (parentId === 'Schedule') {
    return checks.canViewScheduleSubtab(child);
  }

  if (parentId === 'Settings') {
    return checks.canViewTrainerSidebarTabs(child);
  }

  if (parentId === 'People' || parentId === 'Programming') {
    return checks.canViewTrainerTab(child);
  }

  return checks.canViewTrainerTab(child);
};

const filterTrainerNavChildren = (
  parentId: string,
  children: PlatformTab['children'],
  checks: TrainerNavPermissionChecks
): PlatformTab['children'] => {
  if (!children) {
    return undefined;
  }

  if (Array.isArray(children)) {
    return children
      .filter((child) => child.enabled && childCanView(parentId, child, checks))
      .map((child) => filterTrainerNavItem(child, parentId, checks));
  }

  const items = children.items
    ?.filter((child) => child.enabled && childCanView(parentId, child, checks))
    .map((child) => filterTrainerNavItem(child, parentId, checks));

  const sections = children.sections
    ?.map((section) => {
      const sectionChildren = section.children
        .filter((child) => child.enabled && childCanView(parentId, child, checks))
        .map((child) => filterTrainerNavItem(child, parentId, checks));

      if (!sectionChildren.length) {
        return null;
      }

      return {
        ...section,
        children: sectionChildren,
      };
    })
    .filter((section): section is NonNullable<typeof section> => section !== null);

  return {
    ...(items?.length ? { items } : {}),
    ...(sections?.length ? { sections } : {}),
  };
};

const filterTrainerNavItem = (
  item: PlatformTab,
  parentId: string | null,
  checks: TrainerNavPermissionChecks
): PlatformTab => {
  const filteredChildren = item.children
    ? filterTrainerNavChildren(item.id, item.children, checks)
    : undefined;

  return {
    ...item,
    children: filteredChildren,
  };
};

export const filterTrainerNavByPermissions = (
  items: PlatformTab[],
  checks: TrainerNavPermissionChecks
): PlatformTab[] => {
  return items
    .filter((item) => item.enabled && checks.canViewTrainerTab(item))
    .map((item) => filterTrainerNavItem(item, null, checks))
    .filter((item) => {
      if (!PARENTS_REQUIRING_CHILDREN.has(item.id)) {
        return true;
      }

      return hasVisibleChildren(item.children);
    });
};

const decorateDreamcoatNavItem = (
  item: PlatformTab,
  t: TranslateMenu,
  translationPrefix: string,
  client: boolean,
  canViewItem: CanViewItem
): DecoratedNavItem => {
  const routeConfig = client
    ? clientRoutesExtraData[item.id] || routesExtraData[item.id]
    : routesExtraData[item.id];
  const translationKey = `${translationPrefix}.${item.id}`;
  const name =
    item.id === item.name
      ? t(translationKey, { defaultValue: item.name })
      : item.name;

  const decorateChildren = (children: PlatformTab['children']) => {
    if (!children) {
      return undefined;
    }

    if (Array.isArray(children)) {
      return children
        .filter((child) => child.enabled && canViewItem(child))
        .map((child) =>
          decorateDreamcoatNavItem(
            child,
            t,
            translationPrefix,
            client,
            canViewItem
          )
        );
    }

    return {
      items: children.items
        ?.filter((child) => child.enabled && canViewItem(child))
        .map((child) =>
          decorateDreamcoatNavItem(
            child,
            t,
            translationPrefix,
            client,
            canViewItem
          )
        ),
      sections: children.sections?.map((section) => ({
        ...section,
        children: section.children
          .filter((child) => child.enabled && canViewItem(child))
          .map((child) =>
            decorateDreamcoatNavItem(
              child,
              t,
              translationPrefix,
              client,
              canViewItem
            )
          ),
      })),
    };
  };

  const children = decorateChildren(item.children);
  const dreamcoatReactRouteOverride = getDreamcoatReactRouteOverride(item.id, {
    client,
    hasChildren: !!children,
  });

  return {
    ...item,
    name,
    icon: routeConfig?.icon || defaultIcon,
    reactRoute: item.reactRoute ?? dreamcoatReactRouteOverride,
    children,
  };
};

export const decorateDreamcoatNavItems = ({
  canViewItem = canViewAllItems,
  client = false,
  items,
  t,
  translationPrefix,
}: {
  canViewItem?: CanViewItem;
  client?: boolean;
  items: PlatformTab[];
  t: TranslateMenu;
  translationPrefix: string;
}): DecoratedNavItem[] => {
  return items
    .filter((item) => item.enabled && canViewItem(item))
    .map((item) =>
      decorateDreamcoatNavItem(item, t, translationPrefix, client, canViewItem)
    );
};

export const useMainNav = (): DecoratedNavItem[] => {
  const { session, user } = useOptionalSession();
  const {
    canViewTrainerTab,
    canViewScheduleSubtab,
    canViewTrainerSidebarTabs,
    canViewClientTab
  } = usePermissionCheck();

  const { canViewHQTab, canViewHQSidebarTab } = useHQPermissionCheck();

  const { t } = useTranslation('translation', { keyPrefix: 'menu' });

  const ghlFeatureEnabled =
    !!session?.id && hasFeatureFlag(session, FeatureFlag.GoHighLevel);
  const { data: partners = [] } = useQuery({
    queryKey: ['partners'],
    queryFn: fetchUserAuthorizations,
    enabled:
      !!user?.id && !!session?.id && isTrainer(user) && ghlFeatureEnabled,
  });
  const showGhlSyncStatusNav = ghlFeatureEnabled && isGhlConnected(partners);

  if (!user?.id || !session?.id) {
    return [];
  }

  const navType = isTrainer(user) ? 'trainerDashboard' : 'clientDashboard';
  const accountNavType = isTrainer(user) ? 'trainerAccount' : 'clientAccount';

  // Use different permission checks based on platform type
  const isHQ = isHQPlatform(session);
  const canViewTab = isHQ ? canViewHQTab : canViewTrainerTab;
  const canViewSidebarTab = isHQ
    ? canViewHQSidebarTab
    : canViewTrainerSidebarTabs;

  // Load main navigation tabs from API
  let navItems = session?.platformTabs[navType] || [];
  // Load "account" subtabs from API
  let accountItems = session?.platformTabs[accountNavType] || [];

  if (isTrainer(user)) {
    // Dreamcoat trainer_dashboard tabs are permission-filtered server-side
    // (DreamcoatTrainerPlatformTabsFilter). Legacy nav still filters here.
    const dreamcoatTrainerNavFromServer =
      isDreamcoatActive(session, user) && !isHQ;

    if (!dreamcoatTrainerNavFromServer) {
      navItems = navItems.filter((n) => {
        return canViewTab(n);
      });
    }

    if (isDreamcoatActive(session, user)) {
      const canViewDreamcoatTrainerItem = (item: PlatformTab) => {
        return item.id !== 'Email History' || canViewTrainerSidebarTabs(item);
      };

      if (showGhlSyncStatusNav) {
        navItems = appendGhlSyncStatusToDreamcoatNav(navItems, t);
      }

      return decorateDreamcoatNavItems({
        items: filterTrainerNavByPermissions(navItems, {
          canViewTrainerTab,
          canViewTrainerSidebarTabs,
          canViewScheduleSubtab,
        }),
        canViewItem: canViewDreamcoatTrainerItem,
        t,
        translationPrefix: 'trainerDashboard',
      });
    }

    // Apply permissions to account subtabs for trainers
    accountItems = accountItems.filter((n) => {
      return canViewSidebarTab(n);
    });

    if (hasFeatureFlag(session, FeatureFlag.Onboarding)) {
      accountItems = [
        ...accountItems,
        {
          id: 'Onboarding',
          name: 'Onboarding',
          route: '/onboarding',
          enabled: true,
        },
      ];
    }

    if (hasFeatureFlag(session, FeatureFlag.NewDashboard) || user?.newDashboard) {
      accountItems = accountItems.filter((n) => {
        return n.id !== 'Business Dashboard';
      });
    }

    return [
      ...navItems.map((item) => {
        // Get the translation key
        const translationKey = `trainerDashboard.${item.id}`;

        // Check if user has overridden the default name
        // (if id and name don't match, it's overridden)
        const name =
          item.id === item.name
            ? t(translationKey, { defaultValue: item.name })
            : item.name;

        const extraItem = {
          ...item,
          ...routesExtraData[item.id],
          name,
          icon: routesExtraData[item.id]?.icon || defaultIcon,
        };

        // Only add reactRoute for Clients if feature flag is enabled
        if (item.id === 'Clients') {
          return {
            ...extraItem,
            reactRoute: routesExtraData.Clients.reactRoute,
          };
        }

        if (item.id === 'Dashboard') {
          return {
            ...extraItem,
            reactRoute: (hasFeatureFlag(session, FeatureFlag.NewDashboard) || user?.newDashboard)
              ? routesExtraData.Dashboard.reactRoute
              : undefined,
          };
        }

        if (item.id === 'Automations') {
          return {
            ...extraItem,
            reactRoute:
              hasFeatureFlag(session, FeatureFlag.NewAutomations) ||
              isDreamcoatActive(session, user)
                ? routesExtraData.Automations.reactRoute
                : undefined,
          };
        }

        if (item.id === 'Journal') {
          return {
            ...extraItem,
            reactRoute:
              hasFeatureFlag(session, FeatureFlag.NewJournal) ||
              isDreamcoatActive(session, user)
                ? routesExtraData.Journal.reactRoute
                : undefined,
        };
      }

      // Use new React calendar route for Schedule parent if user has opted in
        if (item.id === 'Schedule' && user?.newCalendar) {
          return {
            ...extraItem,
            reactRoute: '/calendar',
            route: undefined,
            children: (
              Array.isArray(extraItem.children) ? extraItem.children : []
            )
              .filter((n) => canViewScheduleSubtab(n))
              .map((child) => {
                const childKey = `schedule.${child.id}`;
                const childName =
                  child.id === child.name
                    ? t(childKey, { defaultValue: child.name })
                    : child.name;

                if (child.id === 'calendar') {
                  return {
                    ...child,
                    name: childName,
                    reactRoute: '/calendar',
                    route: undefined,
                  };
                }

                return {
                  ...child,
                  name: childName,
                };
              }),
          };
        }

        return {
          ...extraItem,
          // Apply permissions to schedule subtabs
          children: (
            Array.isArray(extraItem.children) ? extraItem.children : []
          )
            .filter((n) => {
              if (extraItem.id === 'Schedule') return canViewScheduleSubtab(n);
              return true;
            })
            .map((child) => {
              // For each child, check if it's overridden
              const childKey = `schedule.${child.id}`;
              const childName =
                child.id === child.name
                  ? t(childKey, { defaultValue: child.name })
                  : child.name;

              // Use new React calendar route if user has opted in
              if (child.id === 'calendar' && user?.newCalendar) {
                return {
                  ...child,
                  name: childName,
                  reactRoute: '/calendar',
                  route: undefined,
                };
              }

              return {
                ...child,
                name: childName,
              };
            }),
        };
      }),
      {
        id: 'Account',
        name: t('trainerDashboard.Account', { defaultValue: 'Account' }),
        icon: routesExtraData.Account.icon,
        enabled: true,
        route: '/' + (accountItems[0]?.route?.replaceAll('.', '/') || ''),
        reactRoute: routesExtraData[accountItems[0]?.id]?.reactRoute,
        children: accountItems.map((childRoute) => {
          // For each account item, check if it's overridden
          const childKey = `trainerAccount.${childRoute.id}`;
          const childName =
            childRoute.id === childRoute.name
              ? t(childKey, { defaultValue: childRoute.name })
              : childRoute.name;

          const extraData = routesExtraData[childRoute.id] || {};
          // Recursively merge children from routesExtraData if present
          let mergedChildren = extraData.children
            ? extraData.children.map((grandchild) => ({
                ...grandchild,
                ...(routesExtraData[grandchild.id] || {}),
                ...(routesExtraData[grandchild.id]?.children
                  ? { children: routesExtraData[grandchild.id].children }
                  : {}),
              }))
            : undefined;

          if (childRoute.id === 'Connected Apps' && showGhlSyncStatusNav) {
            mergedChildren = ghlSyncStatusNavChildren(
              childName,
              t(`trainerAccount.${GHL_SYNC_STATUS_TAB_ID}`, {
                defaultValue: 'GoHighLevel',
              })
            );
          }

          return {
            ...childRoute,
            ...extraData,
            route: '/' + childRoute.route.replaceAll('.', '/'),
            name: childName,
            ...(mergedChildren ? { children: mergedChildren } : {}),
          };
        }),
      },
    ];
  } else {
    // Client navigation
    if (isDreamcoatActive(session, user)) {
      return decorateDreamcoatNavItems({
        client: true,
        items: navItems.filter((item) => canViewClientTab(item)),
        t,
        translationPrefix: 'clientDashboard',
      });
    }

    const combinedClientNavItems = getCombinedClientNavItems(
      session?.platformTabs
    ).filter((item) => {
      return canViewClientTab(item);
    });

    const clientExtraData = clientRoutesExtraData;

    return combinedClientNavItems.map((item) => {
      // Get the translation key
      const translationKey = `clientDashboard.${item.id}`;

      // Check if user has overridden the default name
      const name =
        item.id === item.name
          ? t(translationKey, { defaultValue: item.name })
          : item.name;

      const extraItem = {
        ...item,
        ...(clientExtraData[item.id] || routesExtraData[item.id]),
        target: clientExtraData[item.id]?.externalLink ? '_blank' : undefined,
        rel: clientExtraData[item.id]?.externalLink
          ? 'noopener noreferrer'
          : undefined,
        name,
        icon:
          clientExtraData[item.id]?.icon ||
          routesExtraData[item.id]?.icon ||
          defaultIcon,
      };

      // For clients, account items are nested under "My Stuff"
      if (item.id === 'My Stuff') {
        return {
          ...extraItem,
          route: '/' + (accountItems[0]?.route?.replaceAll('.', '/') || ''),
          reactRoute: routesExtraData[accountItems[0]?.id]?.reactRoute,
          children: accountItems.map((childRoute) => {
            // For each account item, check if it's overridden
            const childKey = `clientAccount.${childRoute.id}`;
            const childName =
              childRoute.id === childRoute.name
                ? t(childKey, { defaultValue: childRoute.name })
                : childRoute.name;

            // Use React route for connections if wearable integration feature flag is enabled
            if (childRoute.id === 'My Connected Apps') {
              return {
                ...childRoute,
                ...routesExtraData[childRoute.id],
                name: 'My Connected Apps',
                reactRoute: hasFeatureFlag(
                  session,
                  FeatureFlag.WearableIntegration
                )
                  ? '/connections'
                  : routesExtraData[childRoute.id]?.reactRoute,
                route: '/' + childRoute.route.replaceAll('.', '/'),
              };
            }

            return {
              ...childRoute,
              ...routesExtraData[childRoute.id],
              route: '/' + childRoute.route.replaceAll('.', '/'),
              name: childName,
            };
          }),
        };
      }

      if (item.id === 'Journal') {
        return {
          ...extraItem,
          reactRoute:
            hasFeatureFlag(session, FeatureFlag.NewJournal) ||
            isDreamcoatActive(session, user)
              ? routesExtraData.Journal.reactRoute
              : undefined,
        };
      }

      return extraItem;
    });
  }
};
